/**
 * usePermissions — Slice 1 role gating hook.
 *
 * Pulls the effective `roles` (already dev-override-aware) off AppContext and
 * returns bound helpers so components can write `can("deal.create")` without
 * threading roles through props.
 *
 * UI/UX only — see src/utils/permissions.js. Not a security boundary.
 */
import { useContext, useMemo } from "react";
import { AppContext } from "../AppContextProvider";
import {
  can as canFn,
  canEditField as canEditFieldFn,
  canUploadAttachmentType as canUploadAttachmentTypeFn,
  hasRole as hasRoleFn,
} from "../utils/permissions";

export const usePermissions = () => {
  const ctx = useContext(AppContext);
  const ctxRoles = ctx?.roles;

  return useMemo(() => {
    const roles = Array.isArray(ctxRoles) ? ctxRoles : [];
    return {
      roles,
      /** can(action) -> boolean for the current user's effective roles. */
      can: (action) => canFn(roles, action),
      /** hasRole(role) -> boolean for the current user's effective roles. */
      hasRole: (role) => hasRoleFn(roles, role),
      /**
       * canEditField(field, metaRow) -> may the current user write this deal
       * column. Prefers the server's `editable_for_me` on the metadata row.
       */
      canEditField: (field, metaRow) => canEditFieldFn(roles, field, metaRow),
      /**
       * canUploadAttachmentType(type) -> may the current user file an
       * attachment under this `attachment_type` pick-list value.
       */
      canUploadAttachmentType: (type) =>
        canUploadAttachmentTypeFn(roles, type),
    };
  }, [ctxRoles]);
};

export default usePermissions;
